import {
  ProfileProps,
  SocialLinks,
  DISPLAY_NAME_CHANGE_COOLDOWN_DAYS,
} from "./profile.types";

/**
 * Profile entity.
 * Encapsulates coach profile data and business rules.
 *
 * Pure TypeScript - no React imports.
 */
export class Profile {
  private readonly props: ProfileProps;

  private constructor(props: ProfileProps) {
    this.props = props;
  }

  /**
   * Creates a Profile entity with validation.
   * @throws Error if required fields are missing
   */
  static create(props: ProfileProps): Profile {
    if (!props.email) {
      throw new Error("Profile email is required");
    }

    if (!props.firstName) {
      throw new Error("Profile firstName is required");
    }

    if (!props.lastName) {
      throw new Error("Profile lastName is required");
    }

    return new Profile(props);
  }

  // ==================== Getters ====================

  get id(): number {
    return this.props.id;
  }

  get userId(): number {
    return this.props.userId;
  }

  get email(): string {
    return this.props.email;
  }

  get firstName(): string {
    return this.props.firstName;
  }

  get lastName(): string {
    return this.props.lastName;
  }

  get country(): string {
    return this.props.country;
  }

  get username(): string {
    return this.props.username;
  }

  get displayName(): string | null {
    return this.props.displayName;
  }

  get shortBio(): string {
    return this.props.shortBio;
  }

  get description(): string {
    return this.props.description;
  }

  get avatar(): string | null {
    return this.props.avatar;
  }

  get coverImage(): string | null {
    return this.props.coverImage;
  }

  get bufferTime(): number | null {
    return this.props.bufferTime;
  }

  get howDidYouFindUs(): string | null {
    return this.props.howDidYouFindUs;
  }

  get statusId(): number {
    return this.props.statusId;
  }

  get statusName(): string {
    return this.props.statusName;
  }

  get isSocialAuth(): boolean {
    return this.props.isSocialAuth;
  }

  get isKycVerified(): boolean {
    return this.props.isKycVerified;
  }

  get twoFactorMethod(): "email" | "totp" | null {
    return this.props.twoFactorMethod;
  }

  get isWalletFrozen(): boolean {
    return this.props.isWalletFrozen;
  }

  get suspensionReason(): string | null {
    return this.props.suspensionReason;
  }

  get walletFreezeReason(): string | null {
    return this.props.walletFreezeReason;
  }

  get socialLinks(): SocialLinks {
    return {...this.props.socialLinks};
  }

  get createdAt(): string {
    return this.props.createdAt;
  }

  get updatedAt(): string {
    return this.props.updatedAt;
  }

  get lastDisplayNameChangeAt(): string | null {
    return this.props.lastDisplayNameChangeAt;
  }

  // ==================== Business Methods ====================

  /**
   * Returns a display name for public use.
   * Falls back to firstName + lastName if no display name is set.
   */
  getPublicDisplayName(): string {
    if (this.props.displayName) {
      return this.props.displayName;
    }
    return `${this.props.firstName} ${this.props.lastName}`;
  }

  /**
   * Whether the user has completed onboarding.
   * Onboarding is complete when a username has been set.
   */
  hasCompletedOnboarding(): boolean {
    return !!this.props.username;
  }

  /**
   * Whether the user can change their display name (14-day cooldown).
   * Returns true if no previous change is recorded or the cooldown has elapsed.
   */
  canChangeDisplayName(now: Date = new Date()): boolean {
    if (!this.props.lastDisplayNameChangeAt) {
      return true;
    }

    const lastChange = new Date(this.props.lastDisplayNameChangeAt);
    const cooldownMs = DISPLAY_NAME_CHANGE_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
    return now.getTime() - lastChange.getTime() >= cooldownMs;
  }

  /**
   * Whether the profile is active.
   */
  isActive(): boolean {
    return this.props.statusName === "Active";
  }

  /**
   * Returns the raw properties for serialization or mapping.
   */
  toProps(): ProfileProps {
    return {...this.props, socialLinks: {...this.props.socialLinks}};
  }
}
